/**
 * Which vehicle sides the pipeline recognised, and which it did not.
 *
 * Same gap as partsOverview.js, for sides instead of parts: the redesigned
 * case page showed a tally — "Sides identified 6" — with no list behind it,
 * while the old design (src/pages/CaseDetails.js) lists both sets. Ported
 * from there rather than rewritten, so the two designs keep agreeing on what
 * counts as "identified."
 *
 * A side arrives spelled several ways depending on which collection it came
 * from — "frontLeft", "front_left", "FrontLeft" — so every raw value is
 * normalised to one of eight canonical keys before anything is compared.
 * "Unknown" is dropped outright: it names the absence of a side, not one.
 *
 * The API has never settled on one field for either set, so both are read
 * from every spelling seen in a real response, *and* identified sides are
 * additionally derived from whichever collections actually carry a `side`
 * per item (assessment_details, uploads, roi_images, all_damages) — a side
 * can be identified without the summary ever naming it, if a photo for it
 * exists.
 *
 * Pure, so it can be tested without either page.
 */

const STANDARD_SIDE_OPTIONS = [
  { key: "front", label: "Front" },
  { key: "frontleft", label: "Front Left" },
  { key: "frontright", label: "Front Right" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
  { key: "rear", label: "Rear" },
  { key: "rearleft", label: "Rear Left" },
  { key: "rearright", label: "Rear Right" },
];

const SIDE_LABEL_BY_KEY = STANDARD_SIDE_OPTIONS.reduce((labels, side) => {
  labels[side.key] = side.label;
  return labels;
}, {});

const normalizeArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const uniqueValues = (values) => {
  const seen = new Set();
  return values
    .map((value) => (value ?? "").toString().trim())
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (key === "unknown" || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const normalizeSideKey = (side) => {
  const rawSide =
    typeof side === "string" ? side : side?.side || side?.name || side?.label || "";

  const compact = rawSide
    .toString()
    .trim()
    .toLowerCase()
    .replace(/([a-z])([A-Z])/g, "$1$2")
    .replace(/[\s_-]+/g, "")
    .replace(/^back/, "rear");

  return SIDE_LABEL_BY_KEY[compact] ? compact : "";
};

const getValidSideLabel = (side) => {
  const key = normalizeSideKey(side);
  return key ? SIDE_LABEL_BY_KEY[key] : "";
};

/**
 * @returns {{identified: string[], unidentified: string[]}}
 */
export const sidesOverview = (assessmentData) => {
  const summary = assessmentData?.summary || {};
  const assessmentDetails = normalizeArray(assessmentData?.assessment_details);
  const uploads = normalizeArray(assessmentData?.uploads);
  const roiImages = normalizeArray(assessmentData?.roi_images);
  const allDamages = normalizeArray(assessmentData?.all_damages);
  const warnings = assessmentData?.warnings || {};

  const getValidSideLabels = (values) =>
    uniqueValues(values.map(getValidSideLabel).filter(Boolean));

  const apiUnidentified = getValidSideLabels([
    ...normalizeArray(summary.non_identified_sides_list),
    ...normalizeArray(summary.unidentified_sides_list),
    ...normalizeArray(summary.unidentified_sides),
    ...normalizeArray(summary.missing_sides),
  ]);

  const hasPartialCaptures = normalizeArray(warnings.partial_sides).some(normalizeSideKey);

  const identified = getValidSideLabels([
    ...normalizeArray(summary.identified_sides_list),
    ...normalizeArray(summary.sides_identified_list),
    ...normalizeArray(summary.identified_sides),
    ...assessmentDetails.map((part) => part?.side),
    ...uploads.map((upload) => upload?.side),
    ...roiImages.map((image) => image?.side),
    ...allDamages.map((damage) => damage?.side),
  ]);

  // Every side that could plausibly be "missing" — the eight standard ones,
  // plus anything seen anywhere — unless nothing was ever partially
  // captured, in which case the eight standard sides are the whole universe.
  const sideUniverse = hasPartialCaptures
    ? getValidSideLabels([
        ...STANDARD_SIDE_OPTIONS.map((side) => side.label),
        ...identified,
        ...apiUnidentified,
        ...normalizeArray(warnings.partial_sides),
        ...assessmentDetails.map((part) => part?.side),
        ...uploads.map((upload) => upload?.side),
        ...roiImages.map((image) => image?.side),
        ...allDamages.map((damage) => damage?.side),
      ])
    : STANDARD_SIDE_OPTIONS.map((side) => side.label);

  const identifiedKeys = new Set(identified.map((side) => normalizeSideKey(side)));

  const unidentified = sideUniverse.filter((side) => {
    const key = normalizeSideKey(side);
    return key && !identifiedKeys.has(key);
  });

  return { identified, unidentified };
};

/**
 * True once at least one side has actually been identified.
 *
 * Unlike parts, "unidentified" is never really empty here — with no partial
 * capture the universe defaults to all 8 standard sides, so an all-empty case
 * would still read as "8 unidentified" rather than "nothing to show". What
 * distinguishes a case worth opening the list for is whether anything was
 * confirmed at all.
 */
export const hasSidesOverview = (assessmentData) =>
  sidesOverview(assessmentData).identified.length > 0;

export default sidesOverview;
